<script setup>
import _ from 'lodash'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { axios } from '@/utils/axios'
import Tooltip from '@/components/Tooltip.vue'

const store = useStore()

const props = defineProps({
    platformId: {
        type: String,
        required: true,
    },
    platformUserId: {
        type: String,
        required: true,
    },
    superview: {
        type: Object,
        required: true,
    },
    demographics: {
        type: Object,
        default: null,
    },
    isDemographicsLoading: {
        type: Boolean,
        default: false,
    },
    demographicsProgress: {
        type: Number,
        default: 0,
    },
    demographicsProgressMessage: {
        type: String,
        default: '',
    },
    demographicsError: {
        type: String,
        default: null,
    },
})

const emit = defineEmits(['refresh-demographics'])

const isInsightsLoaded = ref(false)
const insights = ref(null)
const insightsError = ref(null)

const insightsLoadProgress = ref(0)
let insightsProgressInterval

// Track which demographic sections are expanded
const expandedSections = ref({
    countries: false,
    languages: false,
    ethnicity_distribution: false,
    family_status: false,
    income_distribution: false,
    occupation_distribution: false,
    education_level: false,
    engagement_style: false,
    religion_distribution: false,
})

// Helper to get limited items (top 5) when section is collapsed
const getLimitedItems = (items, sectionKey, limit = 5) => {
    if (!items) return []
    if (expandedSections.value[sectionKey]) return items
    return items.slice(0, limit)
}

// Toggle section expansion
const toggleSection = (sectionKey) => {
    expandedSections.value[sectionKey] = !expandedSections.value[sectionKey]
}

const incrementProgress = (progressRef) => {
    progressRef.value = Math.min(progressRef.value + 100 / 90, 100)

    if (progressRef.value >= 100) {
        clearInterval(insightsProgressInterval)
    }
}

const startProgressBar = (progressRef, intervalRef) => {
    progressRef.value = 0
    intervalRef = setInterval(() => incrementProgress(progressRef), 1000)
    return intervalRef
}

const stopProgressBar = (progressRef, intervalRef) => {
    clearInterval(intervalRef)
    progressRef.value = 100
}

onMounted(() => {
    insightsProgressInterval = startProgressBar(insightsLoadProgress, insightsProgressInterval)
    fetchInsights()
})

onUnmounted(() => {
    clearInterval(insightsProgressInterval)
})

const fetchInsights = async (isRefresh = false) => {
    insights.value = null
    insightsError.value = null
    isInsightsLoaded.value = false
    insightsProgressInterval = startProgressBar(insightsLoadProgress, insightsProgressInterval)

    try {
        const insightsResponse = await axios.get(
            `/platforms/${props.platformId}/${props.platformUserId}/audience/insights`,
            {
                params: { refresh: isRefresh },
            },
        )

        isInsightsLoaded.value = true
        stopProgressBar(insightsLoadProgress, insightsProgressInterval)

        if (insightsResponse.data.payload.insights) {
            insights.value = insightsResponse.data.payload.insights
        } else {
            insights.value = false
        }
    } catch (error) {
        console.error('Error fetching insights:', error)
        isInsightsLoaded.value = true
        stopProgressBar(insightsLoadProgress, insightsProgressInterval)
        insightsError.value = 'Failed to load audience insights. Please try again later.'
        insights.value = false
    }
}

// Process demographics from prop — sort/order for display
const processedDemographics = computed(() => {
    if (!props.demographics) return null
    const d = JSON.parse(JSON.stringify(props.demographics))
    if (d.countries) d.countries = _.orderBy(d.countries, ['percentage'], ['desc'])
    if (d.languages) d.languages = _.orderBy(d.languages, ['percentage'], ['desc'])
    if (d.age_ranges) d.age_ranges = _.orderBy(d.age_ranges, ['value'], ['asc'])

    if (d.age_ranges) {
        const idx = _.findIndex(d.age_ranges, (item) => item.value === 'Under 18')
        if (idx !== -1) {
            const item = d.age_ranges.splice(idx, 1)
            if (item.length === 1) d.age_ranges.unshift(item[0])
        }
    }
    return d
})

const getGrade = (score, isNegative = false) => {
    const grades = isNegative ? { A: 0.2, B: 0.4, C: 0.6, D: 0.8, F: 1 } : { F: 0.2, D: 0.4, C: 0.6, B: 0.8, A: 1 }

    for (const [grade, threshold] of Object.entries(grades)) {
        if (score <= threshold) return grade
    }
    return 'F'
}

const getGradeColor = (grade) => {
    return {
        A: 'text-emerald-600',
        B: 'text-emerald-600',
        C: 'text-amber-600',
        D: 'text-orange-600',
        F: 'text-rose-600',
    }[grade]
}

// Calculate overall community health status
const getCommunityHealthStatus = (communityHealth) => {
    if (!communityHealth) return null

    const cohesionGrade = getGrade(communityHealth.cohesion_score)
    const toxicityGrade = getGrade(communityHealth.toxicity_score, true)

    // Both A or B = Excellent
    if (['A', 'B'].includes(cohesionGrade) && ['A', 'B'].includes(toxicityGrade)) {
        return 'excellent'
    }

    // At least one C, but no D or F = Good
    if (cohesionGrade === 'C' || toxicityGrade === 'C') {
        if (!['D', 'F'].includes(cohesionGrade) && !['D', 'F'].includes(toxicityGrade)) {
            return 'good'
        }
    }

    // Has D or F = Needs Attention
    return 'concern'
}

// Calculate overall brand safety status
const getBrandSafetyStatus = (brandSafety) => {
    if (!brandSafety) return null

    const riskGrade = getGrade(brandSafety.overall_risk_level, true)

    // A or B = Excellent (low risk)
    if (['A', 'B'].includes(riskGrade)) {
        return 'excellent'
    }

    // C = Good (moderate risk but manageable)
    if (riskGrade === 'C') {
        return 'good'
    }

    // D or F = Concern (high risk)
    return 'concern'
}

// Calculate audience authenticity status
const getAudienceAuthenticityStatus = (authenticity) => {
    if (!authenticity) return null
    // suspected_bot_percentage is stored 0–1 after normalization
    if (authenticity.suspected_bot_percentage <= 0.2) return 'excellent'
    if (authenticity.suspected_bot_percentage <= 0.4) return 'good'
    return 'concern'
}

// Filter interests to show only high-quality ones (70% or higher)
const filteredInterests = computed(() => {
    if (!insights.value?.interests) return []
    return insights.value.interests.filter(interest => {
        const affinityPercent = 0.5 + interest.relevance_score
        return affinityPercent >= 0.7
    })
})
</script>

<template>
    <div class="mt-8">
        <div class="flex">
            <h2 class="font-semibold text-slate-800">Audience Demographics</h2>
            <div class="ml-2">
                <Tooltip v-if="store.getters.isSfStaff === true">
                    <template #trigger>
                        <button
                            class="btn flex border-slate-200 bg-white p-1 text-slate-700 hover:border-slate-300"
                            @click="emit('refresh-demographics')"
                        >
                            <AppIcon
                                name="arrows-rotate"
                                class="my-auto h-3 w-3 shrink-0 text-slate-500"
                            />
                            <!-- <span class="ml-2 hidden xs:block">Impersonate User</span> -->
                        </button>
                    </template>
                    <template #default>
                        <div class="whitespace-normal text-xs">
                            Clicking this will trigger a manual refresh of the audience analysis.
                        </div>
                    </template>
                </Tooltip>
            </div>
        </div>

        <p class="text-sm">
            Here you can learn more about {{ superview.normalized.display_name }}'s audience using AI analysis. Our AI
            goes through each member of {{ superview.normalized.display_name }}'s audience and learns more about who
            they are and what they are interested in.
        </p>
    </div>

    <div v-if="isDemographicsLoading" class="mt-4">
        <div class="flex flex-col rounded border border-slate-100 px-10 py-32">
            <div class="text-center text-sm">
                <p v-if="demographicsProgressMessage">{{ demographicsProgressMessage }}</p>
                <p v-else>Analyzing audience demographics, this could take up to 2 minutes...</p>
            </div>
            <div class="mt-3 h-2.5 w-full rounded bg-gray-200 dark:bg-gray-700">
                <div class="h-2.5 rounded bg-indigo-500 transition-all duration-300" :style="{ width: `${demographicsProgress}%` }"></div>
            </div>
            <p v-if="demographicsProgress > 0" class="mt-1 text-xs text-indigo-600 text-right">{{ Math.round(demographicsProgress) }}%</p>
        </div>
    </div>

    <div v-else>
        <!--  Demographics -->
        <div v-if="processedDemographics" class="mt-4">
            <div class="grid grid-cols-12 gap-6">
                <!-- Country -->
                <div
                    v-if="processedDemographics.countries"
                    class="col-span-full flex flex-col rounded-sm border border-slate-200 xl:col-span-4"
                >
                    <header class="border-b border-slate-100 px-5 py-4">
                        <h2 class="font-semibold text-slate-800">Countries</h2>
                    </header>

                    <div class="grow p-3">
                        <div class="flex h-full flex-col">
                            <div class="grow">
                                <ul
                                    class="flex justify-between space-x-2 px-2 text-xs font-semibold uppercase text-slate-400"
                                >
                                    <li>Country</li>
                                    <li>Percent</li>
                                </ul>
                                <ul class="mb-4 mt-3 space-y-1 text-sm text-slate-800">
                                    <!-- Item -->
                                    <li
                                        v-for="item in getLimitedItems(processedDemographics.countries, 'countries')"
                                        :key="item.key"
                                        class="relative px-2 py-1"
                                    >
                                        <template v-if="item.key !== 'undefined'">
                                            <div
                                                class="absolute inset-0 bg-indigo-100"
                                                aria-hidden="true"
                                                :style="{
                                                    width: `${$numeral(item.percentage / 100).format('0%')}`,
                                                }"
                                            />
                                            <div class="relative flex justify-between space-x-2">
                                                <div>
                                                    <span v-if="item.key !== 'OTHER'" class="font-semibold uppercase">{{
                                                        item.value
                                                    }}</span>
                                                    <span class="ml-1">
                                                        {{ store.getters.getCountryFromCode(item.value) }}
                                                    </span>
                                                </div>
                                                <div class="font-medium">
                                                    {{ $numeral(item.percentage / 100).format('0.0%') }}
                                                </div>
                                            </div>
                                        </template>
                                    </li>
                                </ul>
                                <!-- Show More/Less Button -->
                                <button
                                    v-if="processedDemographics.countries.length > 5"
                                    class="mt-2 w-full rounded-md px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                                    @click="toggleSection('countries')"
                                >
                                    {{
                                        expandedSections.countries
                                            ? `Show Less`
                                            : `Show ${processedDemographics.countries.length - 5} More`
                                    }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Language -->
                <div
                    v-if="processedDemographics?.languages"
                    class="col-span-full flex flex-col rounded-sm border border-slate-200 xl:col-span-4"
                >
                    <header class="border-b border-slate-100 px-5 py-4">
                        <h2 class="font-semibold text-slate-800">Languages</h2>
                    </header>

                    <div class="grow p-3">
                        <div class="flex h-full flex-col">
                            <div class="grow">
                                <ul
                                    class="flex justify-between space-x-2 px-2 text-xs font-semibold uppercase text-slate-400"
                                >
                                    <li>Language</li>
                                    <li>Percent</li>
                                </ul>
                                <ul class="mb-4 mt-3 space-y-1 text-sm text-slate-800">
                                    <!-- Item -->
                                    <li
                                        v-for="item in getLimitedItems(processedDemographics.languages, 'languages')"
                                        :key="item.key"
                                        class="relative px-2 py-1"
                                    >
                                        <div
                                            class="absolute inset-0 bg-indigo-100"
                                            aria-hidden="true"
                                            :style="{
                                                width: `${$numeral(item.percentage / 100).format('0%')}`,
                                            }"
                                        />
                                        <div class="relative flex justify-between space-x-2">
                                            <div>
                                                <span v-if="item.key !== 'OTHER'" class="font-semibold uppercase">{{
                                                    item.value
                                                }}</span>
                                                <span class="ml-1">{{
                                                    store.getters.getLanguageFromCode(item.value)
                                                }}</span>
                                            </div>
                                            <div class="font-medium">
                                                {{ $numeral(item.percentage / 100).format('0.0%') }}
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                                <!-- Show More/Less Button -->
                                <button
                                    v-if="processedDemographics.languages.length > 5"
                                    class="mt-2 w-full rounded-md px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                                    @click="toggleSection('languages')"
                                >
                                    {{
                                        expandedSections.languages
                                            ? `Show Less`
                                            : `Show ${processedDemographics.languages.length - 5} More`
                                    }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Gender Distribution -->
                <div
                    v-if="processedDemographics?.gender_distribution"
                    class="col-span-full flex flex-col rounded-sm border border-slate-200 xl:col-span-4"
                >
                    <header class="border-b border-slate-100 px-5 py-4">
                        <h2 class="font-semibold text-slate-800">Genders</h2>
                    </header>

                    <div class="grow p-3">
                        <div class="flex flex-col">
                            <div class="grow">
                                <ul
                                    class="flex justify-between space-x-2 px-2 text-xs font-semibold uppercase text-slate-400"
                                >
                                    <li>Gender</li>
                                    <li>Percent</li>
                                </ul>
                                <ul class="mb-4 mt-3 space-y-1 text-sm text-slate-800">
                                    <!-- Item -->
                                    <li
                                        v-for="(item, key) in processedDemographics.gender_distribution"
                                        :key="key"
                                        class="relative px-2 py-1"
                                    >
                                        <div
                                            class="absolute inset-0 bg-indigo-100"
                                            aria-hidden="true"
                                            :style="{
                                                width: `${$numeral(item.percentage / 100).format('0%')}`,
                                            }"
                                        />
                                        <div class="relative flex justify-between space-x-2">
                                            <div>
                                                <span class="capitalize">{{
                                                    key === 'non_binary' ? 'Non Binary' : key
                                                }}</span>
                                            </div>

                                            <div v-if="item.percentage > 1" class="font-medium">
                                                {{ $numeral(item.percentage / 100).format('0%') }}
                                            </div>
                                            <div v-else class="font-medium">&lt;1%</div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Age Distribution -->
                <div
                    v-if="processedDemographics?.age_ranges"
                    class="col-span-full flex flex-col rounded-sm border border-slate-200 xl:col-span-4"
                >
                    <header class="border-b border-slate-100 px-5 py-4">
                        <h2 class="font-semibold text-slate-800">Age Ranges</h2>
                    </header>

                    <div class="grow p-3">
                        <div class="flex flex-col">
                            <div class="grow">
                                <ul
                                    class="flex justify-between space-x-2 px-2 text-xs font-semibold uppercase text-slate-400"
                                >
                                    <li>Age Range</li>
                                    <li>Percent</li>
                                </ul>
                                <ul class="mb-4 mt-3 space-y-1 text-sm text-slate-800">
                                    <!-- Item -->
                                    <li
                                        v-for="item in processedDemographics.age_ranges"
                                        :key="item.value"
                                        class="relative px-2 py-1"
                                    >
                                        <div
                                            class="absolute inset-0 bg-emerald-100"
                                            aria-hidden="true"
                                            :style="{
                                                width: `${$numeral(item.percentage / 100).format('0%')}`,
                                            }"
                                        />
                                        <div class="relative flex justify-between space-x-2">
                                            <div>
                                                <span class="ml-1 capitalize">{{ item.value }}</span>
                                            </div>

                                            <div v-if="item.percentage > 1" class="font-medium">
                                                {{ $numeral(item.percentage / 100).format('0%') }}
                                            </div>
                                            <div v-else class="font-medium">&lt;1%</div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Ethnicity Distribution -->
                <div
                    v-if="processedDemographics?.ethnicity_distribution"
                    class="col-span-full flex flex-col rounded-sm border border-slate-200 xl:col-span-4"
                >
                    <header class="border-b border-slate-100 px-5 py-4">
                        <h2 class="font-semibold text-slate-800">Ethnicities (Beta)</h2>
                    </header>

                    <div class="grow p-3">
                        <div class="flex flex-col">
                            <div class="grow">
                                <ul
                                    class="flex justify-between space-x-2 px-2 text-xs font-semibold uppercase text-slate-400"
                                >
                                    <li>Ethnicity</li>
                                    <li>Percent</li>
                                </ul>
                                <ul class="mb-4 mt-3 space-y-1 text-sm text-slate-800">
                                    <!-- Item -->
                                    <li
                                        v-for="item in getLimitedItems(
                                            processedDemographics.ethnicity_distribution,
                                            'ethnicity_distribution',
                                        )"
                                        :key="item.value"
                                        class="relative px-2 py-1"
                                    >
                                        <div
                                            class="absolute inset-0 bg-emerald-100"
                                            aria-hidden="true"
                                            :style="{
                                                width: `${$numeral(item.percentage / 100).format('0%')}`,
                                            }"
                                        />
                                        <div class="relative flex justify-between space-x-2">
                                            <div>
                                                <span class="ml-1 capitalize">{{ item.value }}</span>
                                            </div>
                                            <div v-if="item.percentage > 1" class="font-medium">
                                                {{ $numeral(item.percentage / 100).format('0%') }}
                                            </div>
                                            <div v-else class="font-medium">&lt;1%</div>
                                        </div>
                                    </li>
                                </ul>
                                <!-- Show More/Less Button -->
                                <button
                                    v-if="processedDemographics.ethnicity_distribution.length > 5"
                                    class="mt-2 w-full rounded-md px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                                    @click="toggleSection('ethnicity_distribution')"
                                >
                                    {{
                                        expandedSections.ethnicity_distribution
                                            ? `Show Less`
                                            : `Show ${processedDemographics.ethnicity_distribution.length - 5} More`
                                    }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Family Status Distribution -->
                <div
                    v-if="processedDemographics?.family_status"
                    class="col-span-full flex flex-col rounded-sm border border-slate-200 xl:col-span-4"
                >
                    <header class="border-b border-slate-100 px-5 py-4">
                        <h2 class="font-semibold text-slate-800">Family Status (Beta)</h2>
                    </header>

                    <div class="grow p-3">
                        <div class="flex flex-col">
                            <div class="grow">
                                <ul
                                    class="flex justify-between space-x-2 px-2 text-xs font-semibold uppercase text-slate-400"
                                >
                                    <li>Status</li>
                                    <li>Percent</li>
                                </ul>
                                <ul class="mb-4 mt-3 space-y-1 text-sm text-slate-800">
                                    <!-- Item -->
                                    <li
                                        v-for="item in getLimitedItems(processedDemographics.family_status, 'family_status')"
                                        :key="item.value"
                                        class="relative px-2 py-1"
                                    >
                                        <div
                                            class="absolute inset-0 bg-emerald-100"
                                            aria-hidden="true"
                                            :style="{
                                                width: `${$numeral(item.percentage / 100).format('0%')}`,
                                            }"
                                        />
                                        <div class="relative flex justify-between space-x-2">
                                            <div>
                                                <span class="ml-1 capitalize">{{ item.value }}</span>
                                            </div>
                                            <div v-if="item.percentage > 1" class="font-medium">
                                                {{ $numeral(item.percentage / 100).format('0%') }}
                                            </div>
                                            <div v-else class="font-medium">&lt;1%</div>
                                        </div>
                                    </li>
                                </ul>
                                <!-- Show More/Less Button -->
                                <button
                                    v-if="processedDemographics.family_status.length > 5"
                                    class="mt-2 w-full rounded-md px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                                    @click="toggleSection('family_status')"
                                >
                                    {{
                                        expandedSections.family_status
                                            ? `Show Less`
                                            : `Show ${processedDemographics.family_status.length - 5} More`
                                    }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Income Distribution -->
                <div
                    v-if="processedDemographics?.income_distribution"
                    class="col-span-full flex flex-col rounded-sm border border-slate-200 xl:col-span-4"
                >
                    <header class="border-b border-slate-100 px-5 py-4">
                        <h2 class="font-semibold text-slate-800">Income Ranges (Beta)</h2>
                    </header>

                    <div class="grow p-3">
                        <div class="flex flex-col">
                            <div class="grow">
                                <ul
                                    class="flex justify-between space-x-2 px-2 text-xs font-semibold uppercase text-slate-400"
                                >
                                    <li>Income Range</li>
                                    <li>Percent</li>
                                </ul>
                                <ul class="mb-4 mt-3 space-y-1 text-sm text-slate-800">
                                    <!-- Item -->
                                    <li
                                        v-for="item in getLimitedItems(
                                            processedDemographics.income_distribution,
                                            'income_distribution',
                                        )"
                                        :key="item.value"
                                        class="relative px-2 py-1"
                                    >
                                        <div
                                            class="absolute inset-0 bg-sky-100"
                                            aria-hidden="true"
                                            :style="{
                                                width: `${$numeral(item.percentage / 100).format('0%')}`,
                                            }"
                                        />
                                        <div class="relative flex justify-between space-x-2">
                                            <div>
                                                <span class="ml-1 capitalize">{{ item.value }}</span>
                                            </div>
                                            <div v-if="item.percentage > 1" class="font-medium">
                                                {{ $numeral(item.percentage / 100).format('0%') }}
                                            </div>
                                            <div v-else class="font-medium">&lt;1%</div>
                                        </div>
                                    </li>
                                </ul>
                                <!-- Show More/Less Button -->
                                <button
                                    v-if="processedDemographics.income_distribution.length > 5"
                                    class="mt-2 w-full rounded-md px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                                    @click="toggleSection('income_distribution')"
                                >
                                    {{
                                        expandedSections.income_distribution
                                            ? `Show Less`
                                            : `Show ${processedDemographics.income_distribution.length - 5} More`
                                    }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Occupation Distribution -->
                <div
                    v-if="processedDemographics?.occupation_distribution"
                    class="col-span-full flex flex-col rounded-sm border border-slate-200 xl:col-span-4"
                >
                    <header class="border-b border-slate-100 px-5 py-4">
                        <h2 class="font-semibold text-slate-800">Occupations (Beta)</h2>
                    </header>

                    <div class="grow p-3">
                        <div class="flex flex-col">
                            <div class="grow">
                                <ul
                                    class="flex justify-between space-x-2 px-2 text-xs font-semibold uppercase text-slate-400"
                                >
                                    <li>Occupation</li>
                                    <li>Percent</li>
                                </ul>
                                <ul class="mb-4 mt-3 space-y-1 text-sm text-slate-800">
                                    <!-- Item -->
                                    <li
                                        v-for="item in getLimitedItems(
                                            processedDemographics.occupation_distribution,
                                            'occupation_distribution',
                                        )"
                                        :key="item.value"
                                        class="relative px-2 py-1"
                                    >
                                        <div
                                            class="absolute inset-0 bg-sky-100"
                                            aria-hidden="true"
                                            :style="{
                                                width: `${$numeral(item.percentage / 100).format('0%')}`,
                                            }"
                                        />
                                        <div class="relative flex justify-between space-x-2">
                                            <div>
                                                <span class="ml-1 capitalize">{{ item.value }}</span>
                                            </div>
                                            <div v-if="item.percentage > 1" class="font-medium">
                                                {{ $numeral(item.percentage / 100).format('0%') }}
                                            </div>
                                            <div v-else class="font-medium">&lt;1%</div>
                                        </div>
                                    </li>
                                </ul>
                                <!-- Show More/Less Button -->
                                <button
                                    v-if="processedDemographics.occupation_distribution.length > 5"
                                    class="mt-2 w-full rounded-md px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                                    @click="toggleSection('occupation_distribution')"
                                >
                                    {{
                                        expandedSections.occupation_distribution
                                            ? `Show Less`
                                            : `Show ${processedDemographics.occupation_distribution.length - 5} More`
                                    }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Education Level Distribution -->
                <div
                    v-if="processedDemographics?.education_level"
                    class="col-span-full flex flex-col rounded-sm border border-slate-200 xl:col-span-4"
                >
                    <header class="border-b border-slate-100 px-5 py-4">
                        <h2 class="font-semibold text-slate-800">Education Level (Beta)</h2>
                    </header>

                    <div class="grow p-3">
                        <div class="flex flex-col">
                            <div class="grow">
                                <ul
                                    class="flex justify-between space-x-2 px-2 text-xs font-semibold uppercase text-slate-400"
                                >
                                    <li>Education Level</li>
                                    <li>Percent</li>
                                </ul>
                                <ul class="mb-4 mt-3 space-y-1 text-sm text-slate-800">
                                    <!-- Item -->
                                    <li
                                        v-for="item in getLimitedItems(processedDemographics.education_level, 'education_level')"
                                        :key="item.value"
                                        class="relative px-2 py-1"
                                    >
                                        <div
                                            class="absolute inset-0 bg-sky-100"
                                            aria-hidden="true"
                                            :style="{
                                                width: `${$numeral(item.percentage / 100).format('0%')}`,
                                            }"
                                        />
                                        <div class="relative flex justify-between space-x-2">
                                            <div>
                                                <span class="ml-1 capitalize">{{ item.value }}</span>
                                            </div>
                                            <div v-if="item.percentage > 1" class="font-medium">
                                                {{ $numeral(item.percentage / 100).format('0%') }}
                                            </div>
                                            <div v-else class="font-medium">&lt;1%</div>
                                        </div>
                                    </li>
                                </ul>
                                <!-- Show More/Less Button -->
                                <button
                                    v-if="processedDemographics.education_level.length > 5"
                                    class="mt-2 w-full rounded-md px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                                    @click="toggleSection('education_level')"
                                >
                                    {{
                                        expandedSections.education_level
                                            ? `Show Less`
                                            : `Show ${processedDemographics.education_level.length - 5} More`
                                    }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Engagement Style Distribution -->
                <div
                    v-if="processedDemographics?.engagement_style"
                    class="col-span-full flex flex-col rounded-sm border border-slate-200 xl:col-span-4"
                >
                    <header class="border-b border-slate-100 px-5 py-4">
                        <h2 class="font-semibold text-slate-800">Engagement Style (Beta)</h2>
                    </header>

                    <div class="grow p-3">
                        <div class="flex flex-col">
                            <div class="grow">
                                <ul
                                    class="flex justify-between space-x-2 px-2 text-xs font-semibold uppercase text-slate-400"
                                >
                                    <li>Engagement Style</li>
                                    <li>Percent</li>
                                </ul>
                                <ul class="mb-4 mt-3 space-y-1 text-sm text-slate-800">
                                    <!-- Item -->
                                    <li
                                        v-for="item in getLimitedItems(
                                            processedDemographics.engagement_style,
                                            'engagement_style',
                                        )"
                                        :key="item.value"
                                        class="relative px-2 py-1"
                                    >
                                        <div
                                            class="absolute inset-0 bg-indigo-100"
                                            aria-hidden="true"
                                            :style="{
                                                width: `${$numeral(item.percentage / 100).format('0%')}`,
                                            }"
                                        />
                                        <div class="relative flex justify-between space-x-2">
                                            <div>
                                                <span class="ml-1 capitalize">{{ item.value }}</span>
                                            </div>
                                            <div v-if="item.percentage > 1" class="font-medium">
                                                {{ $numeral(item.percentage / 100).format('0%') }}
                                            </div>
                                            <div v-else class="font-medium">&lt;1%</div>
                                        </div>
                                    </li>
                                </ul>
                                <!-- Show More/Less Button -->
                                <button
                                    v-if="processedDemographics.engagement_style.length > 5"
                                    class="mt-2 w-full rounded-md px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                                    @click="toggleSection('engagement_style')"
                                >
                                    {{
                                        expandedSections.engagement_style
                                            ? `Show Less`
                                            : `Show ${processedDemographics.engagement_style.length - 5} More`
                                    }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Audience Authenticity Card -->
                <div v-if="processedDemographics?.audience_authenticity" class="col-span-full rounded-lg border border-slate-200 bg-white p-6 xl:col-span-8">
                    <div class="mb-4 flex items-center gap-2">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-5 w-5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                        </svg>
                        <h2 class="text-lg font-semibold">Audience Authenticity</h2>
                    </div>

                    <!-- Excellent Authenticity (Low Risk: 0-20% bots) -->
                    <div v-if="getAudienceAuthenticityStatus(processedDemographics.audience_authenticity) === 'excellent'">
                        <div class="rounded-lg bg-emerald-50 p-4">
                            <div class="flex items-start gap-3">
                                <svg
                                    class="mt-0.5 h-6 w-6 flex-shrink-0 text-emerald-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <div class="flex-1">
                                    <h3 class="font-semibold text-emerald-900">
                                        Highly Authentic Audience
                                    </h3>
                                    <p class="mt-1 text-sm text-emerald-800">
                                        This creator's audience shows strong signs of authenticity. The vast majority of engagement comes from real, genuine users making this an excellent environment for brand partnerships.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Good Authenticity (Moderate Risk: 21-40% bots) -->
                    <div v-else-if="getAudienceAuthenticityStatus(processedDemographics.audience_authenticity) === 'good'">
                        <div class="rounded-lg bg-amber-50 p-4">
                            <div class="flex items-start gap-3">
                                <svg
                                    class="mt-0.5 h-6 w-6 flex-shrink-0 text-amber-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <div class="flex-1">
                                    <h3 class="font-semibold text-amber-900">Moderately Authentic — Some Suspicious Activity</h3>
                                    <p class="mt-1 text-sm text-amber-800">
                                        While the majority of this creator's audience appears genuine, there are some signals of inauthentic engagement that may warrant consideration for brand partnerships.
                                    </p>
                                    <div v-if="processedDemographics.audience_authenticity.bot_signals.length > 0" class="mt-3">
                                        <p class="text-xs font-medium uppercase text-amber-700">Top Suspicious Signals</p>
                                        <ul class="mt-1.5 space-y-1">
                                            <li
                                                v-for="signal in $lodash.orderBy(processedDemographics.audience_authenticity.bot_signals, ['prevalence'], ['desc']).slice(0, 3)"
                                                :key="signal.signal"
                                                class="text-xs text-amber-800"
                                            >
                                                • {{ signal.signal }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Concerning Authenticity (High Risk: 41%+ bots) -->
                    <div v-else>
                        <div class="rounded-lg bg-rose-50 p-4">
                            <div class="flex items-start gap-3">
                                <svg
                                    class="mt-0.5 h-6 w-6 flex-shrink-0 text-rose-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                    />
                                </svg>
                                <div class="flex-1">
                                    <h3 class="font-semibold text-rose-900">Significant Suspicious Activity Detected</h3>
                                    <p class="mt-1 text-sm text-rose-800">
                                        A significant portion of this creator's audience shows signs of inauthentic behavior. This may indicate the presence of bots or fake accounts which could impact the effectiveness of brand partnerships.
                                    </p>

                                    <div class="mt-4">
                                        <div class="mb-3">
                                            <div class="flex items-center justify-between">
                                                <span class="text-xs font-medium uppercase text-gray-600">Suspected Bot Percentage</span>
                                                <span
                                                    :class="[
                                                        getGradeColor(getGrade(processedDemographics.audience_authenticity.suspected_bot_percentage, true)),
                                                        'text-lg font-bold',
                                                    ]"
                                                >
                                                    {{ getGrade(processedDemographics.audience_authenticity.suspected_bot_percentage, true) }}
                                                </span>
                                            </div>
                                            <div class="mt-1.5 text-xs text-gray-600">
                                                Estimated proportion of audience that may be bots or fake accounts
                                            </div>
                                        </div>

                                        <div v-if="processedDemographics.audience_authenticity.bot_signals.length > 0" class="border-t border-rose-200 pt-3">
                                            <p class="text-xs font-medium uppercase text-rose-700">Bot Signals Detected</p>
                                            <div class="mt-2 space-y-2.5">
                                                <div
                                                    v-for="signal in $lodash.orderBy(processedDemographics.audience_authenticity.bot_signals, ['prevalence'], ['desc'])"
                                                    :key="signal.signal"
                                                    class="rounded-lg bg-white p-3"
                                                >
                                                    <div class="flex items-start justify-between gap-2">
                                                        <span class="text-sm font-medium text-rose-900">{{ signal.signal }}</span>
                                                        <span
                                                            :class="[
                                                                getGradeColor(getGrade(signal.prevalence / 100, true)),
                                                                'text-sm font-bold',
                                                            ]"
                                                        >
                                                            {{ getGrade(signal.prevalence / 100, true) }}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-if="processedDemographics.audience_authenticity.reasoning" class="mt-3 border-t border-rose-200 pt-3">
                                            <p class="text-xs font-medium uppercase text-gray-600">AI Analysis</p>
                                            <p class="mt-1.5 text-sm text-gray-700">{{ processedDemographics.audience_authenticity.reasoning }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="!processedDemographics && !isDemographicsLoading" class="mt-4">
            <div v-if="demographicsError" class="flex items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
                <AppIcon name="circle-exclamation" class="h-5 w-5 shrink-0 text-rose-400" />
                <p class="text-sm text-rose-700">{{ demographicsError }}</p>
            </div>
            <div v-else class="rounded border border-slate-100 px-3 py-5">
                <div class="text-md flex">
                    <div class="m-auto text-sm">
                        It seems we don't have enough data to generate an demographics analysis for
                        {{ props.superview.normalized.name }}.
                    </div>
                </div>
            </div>
        </div>

        <template v-if="store.getters.checkPermission('feature.audience.interests')">
            <div class="mt-8">
                <h2 class="font-semibold text-slate-800">Audience Industry Affinities</h2>
                <p class="text-sm">
                    In what other industries does {{ superview.normalized.name }}'s audience spend time watching
                    content?
                </p>

                <div v-if="!isInsightsLoaded && insights === null" class="mt-8">
                    <div class="flex flex-col rounded border border-slate-100 px-10 py-32">
                        <div class="text-center text-sm">
                            <p>Analyzing industry affinities, this could take up to 60 seconds...</p>
                        </div>
                        <div class="mt-3 h-2.5 w-full rounded bg-gray-200 dark:bg-gray-700">
                            <div
                                class="h-2.5 rounded bg-indigo-500"
                                :style="{ width: `${insightsLoadProgress}%` }"
                            ></div>
                        </div>
                    </div>
                </div>

                <div v-else-if="isInsightsLoaded === true && insights" class="mt-4 grid grid-cols-2">
                    <div
                        v-for="{ topic, category, key } in insights.classifications"
                        :key="key"
                        class="mb-2 mr-2 flex flex-col rounded border border-slate-100 px-4 py-3 text-sm"
                    >
                        <div class="grow leading-none">
                            <div class="font-semibold capitalize">
                                {{ category }}
                            </div>
                            <div class="text-xs capitalize">{{ topic }}</div>
                        </div>
                    </div>
                </div>

                <div v-else-if="insightsError && isInsightsLoaded === true" class="mt-4">
                    <div class="flex items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
                        <AppIcon name="circle-exclamation" class="h-5 w-5 shrink-0 text-rose-400" />
                        <p class="text-sm text-rose-700">{{ insightsError }}</p>
                    </div>
                </div>

                <div v-else-if="insights === false && isInsightsLoaded === true" class="mt-4">
                    <div class="rounded border border-slate-100 px-3 py-5">
                        <div class="text-md flex">
                            <div class="m-auto text-sm">
                                It seems we don't have enough data to generate audience insights for
                                {{ props.superview.normalized.name }}.
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-8">
                <h2 class="font-semibold text-slate-800">Audience Interests</h2>
                <p class="text-sm">What is {{ superview.normalized.name }}'s audience interested in?</p>

                <div v-if="!isInsightsLoaded && insights === null" class="mt-8">
                    <div class="flex flex-col rounded border border-slate-100 px-10 py-32">
                        <div class="text-center text-sm">
                            <p>Analyzing audience interests, this could take up to 60 seconds...</p>
                        </div>
                        <div class="mt-3 h-2.5 w-full rounded bg-gray-200 dark:bg-gray-700">
                            <div
                                class="h-2.5 rounded bg-indigo-500"
                                :style="{ width: `${insightsLoadProgress}%` }"
                            ></div>
                        </div>
                    </div>
                </div>

                <div v-else-if="isInsightsLoaded === true && filteredInterests.length > 0" class="mt-4 grid grid-cols-2">
                    <div
                        v-for="{ name, key } in filteredInterests"
                        :key="key"
                        class="mb-2 mr-2 flex flex-col rounded border border-slate-100 px-4 py-3 text-sm"
                    >
                        <div class="flex grow flex-col leading-none">
                            <div class="my-auto font-semibold capitalize">
                                {{ name }}
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else-if="insightsError && isInsightsLoaded === true" class="mt-4">
                    <div class="flex items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
                        <AppIcon name="circle-exclamation" class="h-5 w-5 shrink-0 text-rose-400" />
                        <p class="text-sm text-rose-700">{{ insightsError }}</p>
                    </div>
                </div>

                <div v-else-if="(insights === false || filteredInterests.length === 0) && isInsightsLoaded === true" class="mt-4">
                    <div class="rounded border border-slate-100 px-3 py-5">
                        <div class="text-md flex">
                            <div class="m-auto text-sm">
                                <template v-if="insights === false">
                                    It seems we don't have enough data to generate audience insights for
                                    {{ props.superview.normalized.name }}.
                                </template>
                                <template v-else>
                                    No high-quality audience interests found (showing only interests with 70% relevance or higher).
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
